// Colour scale for the price heat map.
//
// Encoding:
//  * "free" is a STATUS (good), not the bottom of the ramp — it keeps the same
//    green the app uses for free everywhere else, and it is the first legend
//    row, so the legend as a whole still reads green -> purple.
//  * cost is a MAGNITUDE -> a storm/radar ramp: gold -> orange-red -> red ->
//    crimson -> magenta-plum -> violet -> deep purple, strictly darkening.
//  * "can't park here" (over the max stay) is a neutral, off the ramp.
//
// The scale is ADAPTIVE, not a fixed cost/maxCost gradient. Prague has only a
// handful of distinct prices for a given stay (a 1h stay: 10, 15, 20, 30, 40,
// 50, 60, 80 Kč): a linear gradient would render 40 and 60 as near-identical
// reds when the max is 300. Instead:
//  * few distinct prices (<= MAX_DISCRETE) -> each price its own colour, spaced
//    along the ramp, legend lists the exact prices;
//  * more -> prices are grouped by RANK into MAX_DISCRETE bins, legend shows the
//    range in each. So the map paints at most MAX_DISCRETE colours, ever.
//
// Which is why the ramp has exactly 8 stops: at the normal k=8 the painted
// colours ARE the designed colours, with no interpolation blurring them back
// together.
//
// Validated at every reachable sample count (k=2..8) against the light basemap
// #f2f5f8, in normal + deutan + protan + tritan vision (CIEDE2000):
//   * worst adjacent separation 10.0 across k=2..8 — at k=8 it is 10.1, and all
//     7 steps clear 10 in ALL four vision types. The ramp this replaced scored
//     8.8 normal and 3.3 deutan at k=8: that is what "30 and 90 look the same"
//     was, and 3.3 is invisible.
//   * L* 77.6 -> 11.2 (span 66.3), monotonically darkening under all four vision
//     types — so the ordering survives even when hue does not.
//   * vs the blocked grey: 32.7. Contrast vs the basemap: 1.67 -> 15.25.
//
// Two honest limits, both measured rather than assumed:
//  * vs the free green it is 9.2 under protan (36.1 normal). Below the >=15 we
//    wanted, though better than the 8.3 that shipped before. It is structural,
//    not tuning: #2ecc71 simulates to a light tan (L*~76) for red-green
//    colourblind eyes, while the contrast floor pins the lightest paid gold at
//    L*<=79 — same lightness, same hue family. Switching free to the Okabe-Ito
//    bluish green #009e73 lifts this to 15.2 (it carries blue, so it goes
//    grey-green rather than tan) but that is an app-wide change: green=free is
//    also the status colour. Left alone deliberately.
//  * the top rung is a deep violet, not radar's glowing magenta: #ff00ff is
//    LIGHTER than radar red, so a literal NEXRAD tail would break the darkening
//    that carries the ordering.

export const HEAT_FREE = '#2ecc71'
export const HEAT_BLOCKED = '#9aa4b0'

/**
 * Paid ramp, cheap -> expensive. Storm/radar sweep, strictly darkening.
 * Do not "tidy" a stop without re-measuring: several of these are load-bearing
 * in ways that are invisible to normal vision. The chroma profile follows the
 * sRGB gamut envelope (it bulges at red and violet) — flattening it collapses
 * adjacent separation, and evening out the steps by eye tends to wedge a dull
 * tan between two vivid neighbours, which scores well and looks broken.
 */
export const HEAT_RAMP = [
  '#ebb920', // gold      L* 77.6
  '#dd8524', // amber     L* 63.6
  '#e02a07', // orange-red L* 48.9
  '#b5000f', // red       L* 37.6
  '#981733', // crimson   L* 33.0
  '#7f2357', // plum      L* 30.4
  '#690d84', // violet    L* 26.6
  '#31064b', // deep purple L* 11.2
]

/**
 * Above this many distinct prices the scale groups prices into bins.
 *
 * Sized from the real data, not guessed: a normal stay in Prague yields 7-8
 * distinct prices (a 1h stay: 10, 15, 20, 30, 40, 50, 60, 80 Kc), and only a
 * 6h+ stay pushes past that (up to 15 for 12h). At 6 this threshold never
 * fired for any normal stay, so the discrete branch below — the one written
 * for exactly this data — was dead code, and every ordinary map went through
 * binning instead.
 *
 * It doubles as the bin count, so the map never paints more than this many
 * colours and the ramp only ever has to separate this many steps.
 */
export const MAX_DISCRETE = 8

function hexToRgb(h) {
  return [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)]
}
function rgbToHex([r, g, b]) {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`
}

/** Sample the paid ramp at t in [0,1] (linear interpolation between stops). */
export function rampColor(t) {
  const x = Math.max(0, Math.min(1, t)) * (HEAT_RAMP.length - 1)
  const i = Math.floor(x)
  const f = x - i
  if (i >= HEAT_RAMP.length - 1) return HEAT_RAMP[HEAT_RAMP.length - 1]
  const a = hexToRgb(HEAT_RAMP[i])
  const b = hexToRgb(HEAT_RAMP[i + 1])
  return rgbToHex(a.map((v, k) => v + (b[k] - v) * f))
}

/**
 * @typedef {Object} HeatScale
 * @property {('empty'|'discrete'|'binned')} kind
 * @property {function} colorFor  (est: {cost, allowed, fullyFree}) -> colour
 * @property {Array}    legend    discrete: [{color, cost}] — exact prices;
 *                                binned:   [{color, from, to}] — actual cost
 *                                range of the sections in the bin
 * @property {number}   maxCost   the priciest allowed cost on the map
 */

/**
 * Build the adaptive scale for the current set of estimates (one per section).
 * @param {Iterable<{cost:number, allowed:boolean, fullyFree:boolean}>} estimates
 * @returns {HeatScale}
 */
export function buildHeatScale(estimates) {
  const paid = []
  let maxCost = 0
  for (const est of estimates || []) {
    if (!est || !est.allowed || est.fullyFree || est.cost <= 0) continue
    paid.push(est.cost)
    if (est.cost > maxCost) maxCost = est.cost
  }

  const statusColor = (est) => {
    if (!est.allowed) return HEAT_BLOCKED
    if (est.fullyFree || est.cost <= 0) return HEAT_FREE
    return null
  }

  if (paid.length === 0) {
    return { kind: 'empty', maxCost: 0, legend: [], colorFor: (est) => statusColor(est) ?? HEAT_FREE }
  }

  paid.sort((a, b) => a - b)
  const distinct = [...new Set(paid)]

  if (distinct.length <= MAX_DISCRETE) {
    // One well-spaced colour per price. A single price sits high on the ramp so
    // "paid" still reads clearly against the free green.
    const k = distinct.length
    const colorByCost = new Map(
      distinct.map((cost, i) => [cost, rampColor(k === 1 ? 0.7 : i / (k - 1))])
    )
    return {
      kind: 'discrete',
      maxCost,
      legend: distinct.map((cost) => ({ color: colorByCost.get(cost), cost })),
      colorFor: (est) => statusColor(est) ?? colorByCost.get(est.cost) ?? rampColor(est.cost / maxCost),
    }
  }

  // Too many prices to give each its own colour: group them into bins by the
  // RANK of the distinct price, not by section counts.
  //
  // Quantiles over sections is what broke this. Prague's prices are wildly
  // lopsided — for a 3h stay 2880 of 4904 sections cost the same 120 Kc — so
  // every quantile boundary landed inside that one price, the duplicate breaks
  // collapsed, and a single bin swallowed 30/45/60/90 Kc and painted them all
  // the same colour. Ranking the distinct prices cannot collapse: each bin
  // boundary falls between two prices that differ by construction.
  const binCount = Math.min(MAX_DISCRETE, distinct.length)
  const binOfCost = new Map(
    distinct.map((cost, i) => [cost, Math.min(binCount - 1, Math.floor((i * binCount) / distinct.length))])
  )
  const binColor = (i) => rampColor(binCount === 1 ? 0.7 : i / (binCount - 1))

  // Legend shows the ACTUAL cost range inside each bin.
  const ranges = Array.from({ length: binCount }, () => null)
  for (const c of distinct) {
    const i = binOfCost.get(c)
    if (!ranges[i]) ranges[i] = { from: c, to: c }
    else if (c > ranges[i].to) ranges[i].to = c
  }
  const legend = []
  ranges.forEach((r, i) => {
    if (!r) return
    // A bin holding a single price says "72 Kč", not "72–72 Kč": `cost` is what
    // the legend renders when it is set, and from/to stay for ordering.
    const row = { color: binColor(i), from: r.from, to: r.to }
    if (r.from === r.to) row.cost = r.from
    legend.push(row)
  })

  // A price outside the map's own set (shouldn't happen, but colorFor is public)
  // still lands in the right bin rather than falling back to a flat gradient.
  const binIndex = (cost) => {
    if (binOfCost.has(cost)) return binOfCost.get(cost)
    let i = 0
    while (i < distinct.length && distinct[i] < cost) i++
    return binOfCost.get(distinct[Math.min(i, distinct.length - 1)]) ?? 0
  }

  return {
    kind: 'binned',
    maxCost,
    legend,
    colorFor: (est) => statusColor(est) ?? binColor(binIndex(est.cost)),
  }
}
